<template>
  <div class="monitor-active-check">
    <div class="el-input-group el-input-group--append el-input-group--prepend">
      <div v-if="warnings.length" class="el-input-group__prepend">
        <help-tooltip v-if="enabledWarning" type="warning">
          {{ enabledWarning.message }}
        </help-tooltip>
      </div>
      <div class="options-container">
        <el-select
          v-close-on-zoom
          class="is-current-select"
          :value="state.check.isCurrent"
          :disabled="readonly"
          @input="isCurrent => updateCurrent(isCurrent)"
        >
          <el-option :value="false" :label="'Was'" />
          <el-option :value="true" :label="'Is'" />
        </el-select>
        <el-select
          v-close-on-zoom
          :value="state.check.id"
          placeholder="select a vital"
          class="monitor-item-select"
          :disabled="readonly"
          @input="monitorItemKey => updateMonitorItem(monitorItemKey)"
        >
          <el-option
            v-for="monitorItem in monitorItems"
            :key="monitorItem.key"
            :label="monitorItem.label.en"
            :value="monitorItem.key"
          />
        </el-select>
        <help-tooltip v-if="vitalMissingError" type="error">
          {{ vitalMissingError.message }}
        </help-tooltip>
        <el-select
          v-if="isECG"
          v-close-on-zoom
          :value="state.check.ids || []"
          placeholder="any lead"
          multiple
          class="lead-select"
          :disabled="readonly"
          @input="leads => updateLeads(leads)"
        >
          <el-option
            v-for="lead in availableLeads"
            :key="lead.key"
            :label="lead.label.en"
            :value="lead.key"
          />
        </el-select>
        <help-tooltip v-if="leadWarning" type="warning">
          {{ leadWarning.message }}
        </help-tooltip>
      </div>
      <div class="el-input-group__append">
        {{ state.check.isCurrent ? 'being monitored' : 'monitored' }}
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';

import HelpTooltip from '../../common/components/HelpTooltip';

export default {
  name: 'monitor-active-check',
  components: {
    HelpTooltip,
  },
  props: ['treatments', 'readonly', 'emitter', 'ikey', 'getData', 'putData', 'control'],
  data() {
    return {
      state: {
        check: {
          id: null,
          ids: null,
          isCurrent: true,
        },
      },
      monitorItems: [
        {
          key: 'ECG',
          label: {
            en: 'ECG',
          },
        },
        {
          key: 'ETCO2',
          label: {
            en: 'ETCO2',
          },
        },
        {
          key: 'SPO2',
          label: {
            en: 'SPO2',
          },
        },
        {
          key: 'HEART_RATE',
          label: {
            en: 'Heart Rate',
          },
        },
      ],
    };
  },
  computed: {
    componentOptions() {
      const leadTmt = this.emitter.caseConf._treatments.find(t => t.id === 'LEAD_SELECT');
      return leadTmt ? leadTmt.options.componentOptions : [];
    },
    availableLeads() {
      return this.leads.filter(lead =>
        this.componentOptions.LEAD && this.componentOptions.LEAD.availableValues.includes(lead.key),
      );
    },
    leads() {
      const leadSelectTreatment = this.treatments.find(t => t.id === 'LEAD_SELECT');
      if (!leadSelectTreatment) {
        return [];
      }
      const leadComponent = leadSelectTreatment.components.find(c => c.key === 'LEAD');
      if (!leadComponent) {
        return [];
      }
      return leadComponent.metadata.values;
    },
    isECG() {
      const { id } = this.state.check;
      return id === 'ECG';
    },
    errors() {
      if (!this.emitter.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.emitter.$store.getters['validation/errorsByFieldAndId'],
          `caseLogicDescription.${this.control.parent.id}`,
          [],
        ).filter(
          error => error.metadata && error.metadata.checkKey === this.ikey
        ),
        'message',
      );
    },
    warnings() {
      if (!this.emitter.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.emitter.$store.getters['validation/visibleWarningsByFieldAndId'],
          `caseLogicDescription.${this.control.parent.id}`,
          [],
        ).filter(
          warning => warning.metadata && warning.metadata.scope === 'control' && warning.metadata.checkId === this.ikey,
        ),
        'message',
      );
    },
    vitalMissingError() {
      return this.errors.find(error => error.metadata.errorId === 'vitalMissing');
    },
    enabledWarning() {
      return this.warnings.find(warning => warning.metadata.componentId === 'LEAD_SELECT');
    },
    leadWarning() {
      return this.warnings.find(warning => warning.metadata.componentId === 'LEAD');
    },
  },
  beforeMount() {
    this.state.check = { ...this.state.check, ...this.getData(this.ikey) };
    this.emitter.trigger('process', this.control);
    this.update();
  },
  methods: {
    updateCurrent(isCurrent) {
      this.state.check.isCurrent = isCurrent;
      this.state.check.intervals = [];
      this.update();
    },
    updateMonitorItem(monitorItemKey) {
      this.state.check.id = monitorItemKey;
      if (!this.isECG) {
        this.updateLeads(null);
      }
      this.update();
    },
    updateLeads(leads) {
      this.state.check.ids = leads;
      this.update();
    },
    update() {
      this.putData(this.ikey, {
        ...this.state.check,
        type: this.isECG ? 'LEAD_ASSESS' : 'VITAL_ASSESS',
        operator: 'EQUAL',
        value: true,
      });
      this.emitter.trigger('process', this.control);
    },
  },
};
</script>

<style scoped>
.monitor-active-check {
  width: 600px;
}

.options-container {
  display: flex;
  align-items: center;
  background: white;
}

.el-select {
  flex: 1;
}

.help-tooltip {
  margin: 0 4px;
}
</style>
